<template>
  <div id="my-collections">
    <!-- HEADER -->
    <section class="pt-lg-5 py-4">
      <div class="container">
        <div class="row">
          <div class="col-12 text-center">
            <section-header title="My Collections" subtitle="Saved Artwork" />
            <base-button
              @click="handleCreateCollection"
              class="mt-2"
              size="sm"
              type="primary"
              >CREATE COLLECTION</base-button
            >
          </div>
        </div>
      </div>
    </section>

    <!-- COLLECTIONS -->
    <section class="py-4 pb-lg-6">
      <div class="container">
        <div class="row">
          <div class="collections-wrapper col-12">
            <div v-if="user.collections.length > 0">
              <div class="row">
                <div
                  v-for="collection in user.collections"
                  :key="'collection-' + collection.id"
                  class="col-md-4"
                >
                  <div class="collection-card">
                    <router-link
                      :to="'/user/' + user.id + '/collection/' + collection.id"
                    >
                      <div
                        class="collection-card-img"
                        :style="getCollectionCardBackground(collection)"
                      >
                        <div class="collection-card-overlay d-flex flex-column">
                          {{ collection.name }}
                          <div class="collection-card-count bg-gradient-info">
                            {{
                              collection.photos.length +
                              collection.animations.length
                            }}
                          </div>
                        </div>
                      </div>
                    </router-link>
                    <div class="collection-card-footer">
                      <div class="created-date d-flex align-items-center">
                        {{ getDate(collection.created) }}
                        <div v-if="collection.is_public == 1" class="is-public">
                          PUBLIC
                        </div>
                      </div>
                      <div class="collection-actions">
                        <base-button
                          class="action-button"
                          @click="
                            handleEditCollection(
                              collection.id,
                              collection.name,
                              collection.is_public
                            )
                          "
                        >
                          <i class="fas fa-edit"></i>
                        </base-button>
                        <base-button
                          class="action-button"
                          @click="handleDeleteCollection(collection.id)"
                        >
                          <i class="fa fa-trash-alt"></i>
                        </base-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="no-collections">
              You haven't created any collections yet. Use the button above to
              create your first collection.
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { SectionHeader } from "../../components";
import { mapState } from "vuex";
import swal from "sweetalert2";

const apiBaseURL = process.env.VUE_APP_API_URL;

export default {
  name: "my-collections-page",
  metaInfo: {
    title: "My Collections - Art Grab",
  },
  components: {
    SectionHeader,
  },
  data() {
    return {};
  },
  methods: {
    handleDeleteCollection(id) {
      swal
        .fire({
          title: "ARE YOU SURE?",
          text: "Are you sure you want to delete this collection?",
          type: "warning",
          showCancelButton: true,
          confirmButtonColor: "#000",
          cancelButtonColor: "#f5365c",
          confirmButtonText: "YES, DELETE IT",
        })
        .then((result) => {
          if (result.value) {
            if (id > 0) {
              this.axios
                .request({
                  baseURL: apiBaseURL,
                  url: "/api/artv1.1/user/deletecollection",
                  method: "post",
                  data: {
                    id: id,
                  },
                })
                .then((result) => {
                  if (result.data.data.message == "Success") {
                    this.$store.dispatch("getCollections");
                    swal.fire({
                      type: "success",
                      title: "Success!",
                      text: "The collection has been deleted.",
                      confirmButtonColor: "#000",
                    });
                  } else {
                    this.showErrorAlert(
                      "There was a problem deleting this collection. Please Try Again."
                    );
                  }
                })
                .catch((error) => {
                  this.showErrorAlert(
                    `There was a problem deleting this collection: ${error}`
                  );
                });
            }
          }
        });
    },
    showSuccessAlert(closeAction) {
      swal.fire({
        type: "success",
        title: "Success!",
        text: "The image details have been updated.",
        confirmButtonColor: "#000",
        onClose: closeAction,
      });
    },
    showErrorAlert(message, closeAction) {
      swal.fire({
        type: "error",
        title: "Oh no!",
        text: message,
        confirmButtonColor: "#000",
        onClose: closeAction || null,
      });
    },
    handleEditCollection(id, name, is_public) {
      this.$store.commit("showEditCollectionModal", {
        status: true,
        name,
        id,
        is_public: is_public == 1 ? true : false,
      });
    },
    getDate(date) {
      const month = date.substr(5, 2);
      const day = date.substr(8, 2);
      const year = date.substr(0, 4);
      return `${month}-${day}-${year}`;
    },
    handleCreateCollection() {
      this.$store.commit("showCreateCollectionModal", true);
    },
    getCollectionCardBackground(collection) {
      if (collection && collection.photos.length > 0) {
        return `background-image: url('${collection.photos[0].url_path}')`;
      } else if (collection && collection.animations.length > 0) {
        return `background-image: url('${collection.animations[0].poster_url_path}')`;
      }
      return `background: black`;
    },
  },
  mounted() {
    this.$store.commit("setLayout", "page-layout");
    this.$store.dispatch("getCollections");
  },
  computed: {
    ...mapState(["user"]),
  },
};
</script>

<style scoped lang="scss">
#my-collections {
  padding-top: 100px;

  .no-collections {
    padding: 4.5rem 1.5rem;
    color: #666;
    background: #f7f7f7;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .collection-card {
    text-align: center;
    margin-bottom: 2rem;

    .collection-card-img {
      background-color: black;
      background-size: cover;
      background-position: center;
      border-radius: 3px;
      overflow: hidden;

      .collection-card-overlay {
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        color: white;
        font-size: 1.25rem;
        padding: 30px;
        font-weight: 600;
        height: 290px;

        .collection-card-count {
          font-size: 0.75rem;
          text-transform: uppercase;
          padding: 5px 12px;
          border-radius: 25px;
          width: 25px;
          height: 25px;
          display: flex;
          justify-content: center;
          align-items: center;
          margin-top: 6px;
        }
      }
    }

    .collection-card-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.55rem 0 0;
      width: 100%;
      margin-bottom: 4.5rem;
      color: grey;

      .created-date {
        font-size: 0.75rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-top: 6px;
      }

      .is-public {
        font-size: 0.5rem;
        font-weight: bold;
        color: white;
        background-color: #2dce89;
        padding: 0.5px 8px;
        border-radius: 10px;
        letter-spacing: 0.5px;
        line-height: 14px;
        cursor: default;
        margin-left: 6px;
      }

      .collection-actions {
        .action-button {
          padding: 0;
          background: none;
          border: 0;
          box-shadow: none;
          margin-left: 12px;

          i {
            color: grey;
            font-size: 16px;
          }
        }
      }
    }
  }
}

@media (max-width: 991.98px) {
  #my-collections {
    padding-top: 100px;
  }
}
</style>
